import { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { authApi } from '@/api/auth';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { CODE_LENGTH, CodeInput } from '@/components/ui/CodeInput';
import { humanizeError } from '@/lib/errors';
import { notifyError } from '@/lib/haptics';
import { safeNext } from '@/lib/guestMode';
import { loginPathWithNext, studentLanding } from '@/lib/loginReturn';
import { maskEmail } from './VerifyEmailPage';
import { AuthShell } from './LoginPage';

/** `next` — куда гость нажимал до регистрации (гостевой режим, 14.09.2026); проверяет safeNext. */
type Arrival = { email?: string; codeAlreadySent?: boolean; next?: unknown };

// Ученик — `studentLanding()`: в приложении «Обучение», на сайте главная (27.09.2026).
function landingFor(role?: string): string {
  return role === 'admin' || role === 'manager' ? '/admin' : role === 'curator' ? '/curator' : studentLanding();
}

function messageOf(e: unknown): string {
  return (e as { message?: string })?.message ?? '';
}

/**
 * Вход по коду из письма — он же последний шаг регистрации.
 *
 * Пароля в этой платформе у ученика может не быть вовсе: он вводит имя, почту и
 * телефон, а дальше подтверждает почту кодом. Поэтому экран один и тот же для
 * двух путей — «только что зарегистрировался» (письмо уже ушло) и «вхожу по
 * коду» (письмо просим здесь).
 */
export function EnterCodePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((s) => s.setAuth);
  const currentUser = useAuthStore((s) => s.user);
  const isAuthed = useAuthStore((s) => Boolean(s.accessToken));

  const arrival = (location.state ?? {}) as Arrival;
  const email = (arrival.email ?? '').trim();
  // Куда вести ученика после кода, если он пришёл из гостевого режима. Только /app/….
  const next = safeNext(arrival.next);

  const [code, setCode] = useState('');
  const [resetKey, setResetKey] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [cooldown, setCooldown] = useState(arrival.codeAlreadySent ? 60 : 0);

  const resend = useCallback(async () => {
    setError(null);
    setNotice(null);
    try {
      const { retry_after_seconds } = await authApi.requestLoginCode(email);
      setCooldown(retry_after_seconds || 60);
      setNotice(t('enterCode.sent'));
    } catch (e) {
      setError(humanizeError(e));
    }
  }, [email, t]);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  const submit = useCallback(
    async (value: string) => {
      setSubmitting(true);
      setError(null);
      setNotice(null);
      try {
        const resp = await authApi.loginByCode(email, value);
        setAuth(resp.user, resp.access_token, resp.refresh_token);
        // Ученик, пришедший из гостевого режима, попадает туда, куда нажимал (курс, словарь);
        // сотрудник — всегда в свой кабинет. Чужой адрес safeNext не пропускает.
        const target = resp.user.role === 'student' ? next : null;
        navigate(target ?? landingFor(resp.user.role), { replace: true });
      } catch (e) {
        notifyError();
        const message = messageOf(e);
        setError(
          message === 'too_many_attempts'
            ? t('enterCode.errAttempts')
            : message === 'invalid_code'
              ? t('enterCode.errInvalid')
              : humanizeError(e),
        );
        setCode('');
        setResetKey((k) => k + 1);
      } finally {
        setSubmitting(false);
      }
    },
    [email, navigate, next, setAuth, t],
  );

  // Уже вошёл — кода не нужно.
  if (isAuthed && currentUser) return <Navigate to={landingFor(currentUser.role)} replace />;
  // Прямой заход по адресу без почты (обновление страницы, закладка): начинать
  // такой экран не с чего — отправляем туда, где почту спрашивают.
  if (!email) return <Navigate to="/login-code" replace />;

  return (
    // Без стрелки «назад»: она вернула бы человека в форму регистрации, где
    // аккаунт уже создан, — повторная отправка дала бы «почта занята». Уйти с
    // экрана есть чем: «другая почта» ведёт к вводу адреса заново.
    <AuthShell title={t('enterCode.title')} back={false}>
      <div className="space-y-5">
        <p className="text-sm text-on-surface-variant">
          {t('enterCode.sentTo', { email: maskEmail(email) })}
        </p>

        <CodeInput
          onComplete={(value) => void submit(value)}
          onChange={setCode}
          disabled={submitting}
          resetKey={resetKey}
        />

        {error && <p className="text-sm text-error">{error}</p>}
        {!error && notice && <p className="text-sm text-success">{notice}</p>}
        <p className="text-xs text-on-surface-variant">{t('enterCode.hint')}</p>

        <Button
          type="button"
          size="lg"
          fullWidth
          loading={submitting}
          disabled={code.length < CODE_LENGTH}
          onClick={() => void submit(code)}
        >
          {t('enterCode.submit')}
        </Button>

        <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
          <button
            type="button"
            onClick={() => void resend()}
            disabled={cooldown > 0}
            className="font-semibold text-primary disabled:text-on-surface-variant"
          >
            {cooldown > 0 ? t('enterCode.resendIn', { seconds: cooldown }) : t('enterCode.resend')}
          </button>
          <button
            type="button"
            onClick={() => navigate('/login-code', { replace: true, state: next ? { next } : undefined })}
            className="text-on-surface-variant hover:text-primary"
          >
            {t('enterCode.changeEmail')}
          </button>
        </div>
      </div>
    </AuthShell>
  );
}

/**
 * Первый шаг входа по коду: спрашиваем почту. Отдельный экран, а не поле на
 * предыдущем, потому что сюда же возвращает ссылка «другая почта».
 */
export function LoginCodePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useAuthStore((s) => s.user);
  const isAuthed = useAuthStore((s) => Boolean(s.accessToken));
  // Возврат на урок, с которого выбросило (техдолг 299), — пришёл с экрана пароля; дальше едет к коду.
  const next = safeNext((useLocation().state as { next?: unknown } | null)?.next);

  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (isAuthed && currentUser) return <Navigate to={landingFor(currentUser.role)} replace />;

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await authApi.requestLoginCode(email);
      // Сервер отвечает одинаково на любой адрес — и мы ведём дальше одинаково.
      // Если ученика с такой почтой нет, письма просто не будет: сказать об этом
      // на экране значило бы отдать перебором список учеников школы.
      navigate('/enter-code', { state: { email, codeAlreadySent: true, ...(next ? { next } : {}) } });
    } catch (err) {
      setError(humanizeError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell title={t('loginCode.title')}>
      <form onSubmit={onSubmit} className="space-y-4">
        <p className="text-sm text-on-surface-variant">{t('loginCode.subtitle')}</p>
        <input
          type="email"
          required
          autoComplete="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@gmail.com"
          aria-label={t('auth.email')}
          className="h-12 w-full rounded-button bg-surface-container-lowest px-4 text-on-surface shadow-soft focus:outline-none focus:shadow-ambient"
        />
        {error && <p className="text-sm text-error">{error}</p>}
        <Button type="submit" size="lg" fullWidth loading={submitting}>
          {t('loginCode.submit')}
        </Button>
        <button
          type="button"
          onClick={() => navigate(next ? loginPathWithNext(next) : '/login')}
          className="w-full text-center text-sm text-on-surface-variant hover:text-primary"
        >
          {t('loginCode.usePassword')}
        </button>
      </form>
    </AuthShell>
  );
}
